import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Drawer,
  Tooltip,
  TextField,
  InputAdornment,
  Fab,
  Badge,
  Zoom,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Menu as MenuIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Search as SearchIcon,
  ShoppingCart as ShoppingCartIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import useStoreInfo from '../../../hooks/useCompanyInfo';
import { fetchSuppliers, addSupplierToStore } from '../../../app/store/slices/supplierSlice';
import { fetchProductBrands, addBrandToStore } from '../../../app/store/slices/productBrandSlice';
import { fetchProductCategories, addCategoryToStore } from '../../../app/store/slices/productCategorySlice';

import CategoryPanel from '../components/CategoryPanel';
import ProductGrid from '../components/ProductGrid';
import CartPanel from '../components/CartPanel';
import PreviewDialog from '../components/PreviewDialog';
import CustomerDialog from '../components/CustomerDialog';
import PrintOptionsDialog from '../components/PrintOptionsDialog';
import A4PreviewDialog from '../components/A4PreviewDialog';
import AddProductDialog from '../../product/components/AddProductDialog';

import { searchProducts, getProductsPaginated } from '../../product/services/productService';
import { A4InvoiceTemplate } from '../components/A4InvoiceTemplate';
import { getThermalReceiptTemplate } from '../components/ThermalReceiptTemplate';
import { getVatRates } from '../../companysettings/services/vatService';
import { createTicket, validateTicketData, transformCartToTicketDto } from '../services/ticketDeCaisseService';

function generateReceiptNumber() {
  const prefix = 'TKT';
  const timestamp = Date.now().toString().slice(-8);
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `${prefix}-${timestamp}${random}`;
}

const PosListPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const posContainerRef = useRef(null);

  const { storeInfo } = useStoreInfo();
  
  // ✅ Get suppliers, brands, and categories from Redux store
  const suppliersFromStore = useSelector((state) => state.suppliers.suppliers) || [];
  const brandsFromStore = useSelector((state) => state.productBrands.brands) || [];
  const categoriesFromStore = useSelector((state) => state.productCategories.categories) || [];

  // State Management
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [customVATRate, setCustomVATRate] = useState(21);
  const [vatRates, setVatRates] = useState([]);
  
  // Dialog States
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);

  // Payment State
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('CASH');
  const [paidAmount, setPaidAmount] = useState('');
  const [receiptNumber, setReceiptNumber] = useState(generateReceiptNumber());

  // Fullscreen State
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [printOptionsOpen, setPrintOptionsOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [currentFormat, setCurrentFormat] = useState('receipt');

  const [discount, setDiscount] = useState(null);

  // ✅ Log store info when it changes
  useEffect(() => {
    if (storeInfo) {
      console.log('🏪 Store info available in POS:', storeInfo);
    } else {
      console.warn('⚠️ No store info available in Redux store');
    }
  }, [storeInfo]);
  
  // ✅ Fetch suppliers, brands, and categories if not in store
  useEffect(() => {
    if (suppliersFromStore.length === 0) {
      dispatch(fetchSuppliers());
    }
    if (brandsFromStore.length === 0) {
      dispatch(fetchProductBrands());
    }
    if (categoriesFromStore.length === 0) {
      dispatch(fetchProductCategories());
    }
  }, [dispatch, suppliersFromStore.length, brandsFromStore.length, categoriesFromStore.length]);

  useEffect(() => {
    fetchProducts();

    // Add fullscreen change event listener
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // ✅ UPDATED: Fetch top 100 products using pagination
  const fetchProducts = async () => {
    try {
      setLoading(true);
      console.log('📦 Fetching top 100 products for POS...');
      
      // ✅ Use paginated endpoint to get top 100 products
      const response = await getProductsPaginated({
        pageNumber: 1,
        pageSize: 100,
        sortBy: 'Name',
        sortDirection: 'Asc'
      });
      
      console.log('✅ POS Pagination API Response:', response);
      
      let productsData = [];
      
      // ✅ Handle response structure
      if (response?.payload?.Items) {
        productsData = response.payload.Items;
        console.log('📦 Mapped from response.payload.Items');
      } else if (response?.payload?.items) {
        productsData = response.payload.items;
        console.log('📦 Mapped from response.payload.items');
      } else if (response?.Items) {
        productsData = response.Items;
        console.log('📦 Mapped from response.Items');
      } else if (response?.items) {
        productsData = response.items;
        console.log('📦 Mapped from response.items');
      } else if (response?.payload && Array.isArray(response.payload)) {
        productsData = response.payload;
        console.log('📦 Mapped from response.payload array');
      } else if (response?.data && Array.isArray(response.data)) {
        productsData = response.data;
        console.log('📦 Mapped from response.data array');
      } else if (Array.isArray(response)) {
        productsData = response;
        console.log('📦 Mapped from direct array');
      }

      // ✅ Map products to ensure consistent structure for POS
      const mappedProducts = productsData.map(product => ({
        Id: product.Id,
        Name: product.Name,
        SKU: product.SKU || product.BarCode,
        BarCode: product.BarCode,
        Description: product.Description,
        SalePrice: product.SalePrice,
        CostPrice: product.CostPrice,
        MinSalePrice: product.MinSalePrice,
        AveragePrice: product.AveragePrice,
        StockQuantity: product.StockQuantity,
        MinStockLevel: product.MinStockLevel,
        IsActive: product.IsActive,
        IsPromotionProduct: product.IsPromotionProduct,
        PromotionPercentage: product.PromotionPercentage,
        // ✅ Extract nested category data
        ProductCategoryId: product.ProductCategoryId || product.CategoryId,
        ProductCategory: product.ProductCategory || (product.CategoryName ? {
          CategoryName: product.CategoryName
        } : null),
        // ✅ Extract nested supplier data
        SupplierId: product.SupplierId,
        Supplier: product.Supplier || (product.SupplierName ? {
          Name: product.SupplierName
        } : null),
        // ✅ Extract nested brand data
        ProductBrandId: product.ProductBrandId || product.BrandId,
        ProductBrand: product.ProductBrand || (product.BrandName ? {
          BrandName: product.BrandName
        } : null),
        ModelNumber: product.ModelNumber,
        ImageUrl: product.ImageUrl,
        SyncToShopify: product.SyncToShopify,
        IsWishlistProduct: product.IsWishlistProduct,
        CreatedDate: product.CreatedDate,
        ModifiedDate: product.ModifiedDate,
        // ✅ For backward compatibility with existing POS code
        Price: product.SalePrice,
      }));

      console.log(`📦 Fetched and mapped ${mappedProducts.length} products for POS`);
      console.log('📦 Sample product:', mappedProducts[0]);
      
      setProducts(mappedProducts);
      setFilteredProducts(mappedProducts);
      
      if (mappedProducts.length > 0) {
        extractCategoriesAndSetDefault(mappedProducts);
      } else {
        console.warn('⚠️ No products loaded');
        toast.warning(t('no_products_available') || 'No products available');
      }
    } catch (error) {
      console.error('❌ Error fetching products:', error);
      toast.error(t('failed_to_load_products') || 'Failed to load products');
      setProducts([]);
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ New function to extract categories and set "All Products" as default
  const extractCategoriesAndSetDefault = (productsArray) => {
    console.log('📦 Total Products:', productsArray.length);
    
    const categoryMap = new Map();
    
    // Extract unique categories with their product counts
    productsArray.forEach(product => {
      const categoryId = product.ProductCategoryId;
      const categoryName = product.ProductCategory?.CategoryName;
      
      if (categoryId && categoryName) {
        const trimmedName = categoryName.trim();
        if (!categoryMap.has(categoryId)) {
          categoryMap.set(categoryId, {
            id: categoryId,
            name: trimmedName,
            count: 0
          });
        }
        categoryMap.get(categoryId).count++;
      }
    });

    // Create "All Products" category
    const allProductsCategory = { 
      id: 'all', 
      name: t('all_products') || 'All Products', 
      icon: '🛍️',
      count: productsArray.length 
    };

    // Convert map to array and add icons
    const otherCategories = Array.from(categoryMap.values()).map(cat => ({
      ...cat,
      icon: getCategoryIcon(cat.name)
    }));

    const categoriesArray = [allProductsCategory, ...otherCategories];
    setCategories(categoriesArray);
    
    // ✅ Set "All Products" as default selected category
    setSelectedCategory(allProductsCategory);
    
    console.log('✅ Categories extracted, "All Products" set as default');
  };

  const getCategoryIcon = (categoryName) => {
    const iconMap = {
      'GENERAL': '📦',
      'PC': '💻',
      'CLAVIERS': '⌨️',
      'ECRANS': '🖥️',
      'MEMOIRES': '💾',
      'DISQUE DUR': '💿',
      'SACS/HOUSSES': '💼',
      'ACCESSOIRES': '🔌',
      'ADAPTATEURS': '🔌',
      'RESEAU': '🌐',
      'BOULANGERIE': '🥖',
      'FRUITS': '🍎',
      'ELECTRONICS': '📱',
      'CLOTHING': '👕',
      'FOOD': '🍔',
      'DRINKS': '🥤',
      'BOOKS': '📚',
    };

    const upperCat = (categoryName || '').toUpperCase();
    for (const [key, icon] of Object.entries(iconMap)) {
      if (upperCat.includes(key)) {
        return icon;
      }
    }
    return '📦';
  };

  // ✅ Update the filterProducts function:
  const filterProducts = () => {
    // Don't filter if search is active
    if (searchQuery.trim()) {
      return;
    }

    let filtered = [...products];

    console.log('🔍 Filter - Selected Category:', selectedCategory);
    console.log('🔍 Filter - Total Products:', products.length);

    // Apply category filter
    if (selectedCategory && selectedCategory.id !== 'all') {
      filtered = filtered.filter(p => {
        const productCategoryId = p.ProductCategoryId;
        return productCategoryId === selectedCategory.id;
      });
      console.log('📊 Filtered by category:', filtered.length, 'products');
    }

    console.log('✅ Final Filtered Products:', filtered.length);
    setFilteredProducts(filtered);
  };

  // ✅ FIXED: Update addToCart with proper rounding
  const addToCart = (product, quantity = 1) => {
    const existingIndex = cart.findIndex(item => item.Id === product.Id);

    // ✅ Get the price including VAT (this is the displayed sale price)
    const priceInclVAT = product.IsPriceEdited 
      ? product.SalePrice  // This is the edited price from ProductGrid
      : (product.SalePrice || product.Price || 0);
    
    const vatRate = customVATRate !== null ? customVATRate : 21;
    
    // ✅ FIXED: Calculate base price excluding VAT correctly WITHOUT premature rounding
    // Formula: PriceExcl = PriceIncl / (1 + VAT/100)
    const basePrice = priceInclVAT / (1 + vatRate / 100);

    console.log('🛒 Adding to cart:', {
      productName: product.Name,
      originalPrice: product.OriginalPrice,
      editedPrice: product.IsPriceEdited ? product.SalePrice : null,
      priceInclVAT: priceInclVAT,
      vatRate: vatRate,
      basePrice: basePrice,
      basePriceRounded: Number(basePrice.toFixed(4)), // ✅ Keep more precision
      quantity: quantity,
      isPriceEdited: product.IsPriceEdited,
      calculation: `${priceInclVAT} / (1 + ${vatRate}/100) = ${basePrice}`,
    });

    if (existingIndex !== -1) {
      // ✅ Product exists in cart - update quantity AND price if it was edited
      const updatedCart = [...cart];
      
      // If the price was edited, update the price for existing item too
      if (product.IsPriceEdited) {
        updatedCart[existingIndex] = {
          ...updatedCart[existingIndex],
          Quantity: updatedCart[existingIndex].Quantity + quantity,
          Price: Number(basePrice.toFixed(4)), // ✅ Store with 4 decimal precision
          PriceInclVAT: Number(priceInclVAT.toFixed(2)),
          VAT: vatRate,
          OriginalPrice: product.OriginalPrice,
          IsPriceEdited: true,
        };
        toast.success(`${product.Name} ${t('quantity_updated') || 'quantity updated'} with new price €${priceInclVAT.toFixed(2)}`);
      } else {
        updatedCart[existingIndex].Quantity += quantity;
        toast.success(`${product.Name} ${t('quantity_updated') || 'quantity updated'}`);
      }
      
      setCart(updatedCart);
    } else {
      // ✅ New product - add to cart with correctly calculated base price
      setCart([
        ...cart,
        {
          ...product,
          Quantity: quantity,
          Price: Number(basePrice.toFixed(4)), // ✅ Store with 4 decimal precision
          PriceInclVAT: Number(priceInclVAT.toFixed(2)),
          VAT: vatRate,
          OriginalPrice: product.IsPriceEdited ? product.OriginalPrice : priceInclVAT,
          IsPriceEdited: product.IsPriceEdited || false,
          _rowKey: Date.now() + Math.random(),
        },
      ]);
      
      if (product.IsPriceEdited) {
        toast.success(`${product.Name} ${t('added_to_cart') || 'added to cart'} with custom price €${priceInclVAT.toFixed(2)}`);
      } else {
        toast.success(`${product.Name} ${t('added_to_cart') || 'added to cart'}`);
      }
    }
  };

  const updateCartQuantity = (rowKey, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(rowKey);
      return;
    }

    setCart(cart.map(item =>
      item._rowKey === rowKey
        ? { ...item, Quantity: newQuantity }
        : item
    ));
  };

  const removeFromCart = (rowKey) => {
    setCart(cart.filter(item => item._rowKey !== rowKey));
    toast.info(t('item_removed') || 'Item removed');
  };

  const clearCart = () => {
    setCart([]);
    setCustomer(null);
    setPaidAmount('');
    setDiscount(null);
    setCustomVATRate(21); // ✅ Reset to 21% instead of 0
    setReceiptNumber(generateReceiptNumber());
    toast.info(t('cart_cleared') || 'Cart cleared');
  };
  
  // Handler for when a product is created from the dialog
  const handleProductCreated = (createdProduct) => {
    if (createdProduct && createdProduct.Id) {
      // Add the newly created product to cart
      addToCart(createdProduct, 1);
      toast.success(t('product_added_to_cart') || 'Product added to cart');
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSearchQuery(''); // Clear search when changing category
    if (isMobile || isTablet) {
      setCategoryDrawerOpen(false);
    }
  };

  const handlePreview = () => {
    if (cart.length === 0) {
      toast.error(t('please_add_products') || 'Please add at least one product');
      return;
    }
    
    console.log('🔍 Preview - Store Info at Preview Time:', storeInfo);
    
    setPrintOptionsOpen(true); // ✅ Open print options dialog instead
  };

  const handlePrint = () => {
    if (cart.length === 0) {
      toast.error(t('please_add_products') || 'Please add at least one product');
      return;
    }
    
    setPrintOptionsOpen(true); // ✅ Open print options dialog instead
  };

  // ✅ Update handleFormatSelection to handle both preview and print correctly
  const handleFormatSelection = async (format, action) => {
    setPrintOptionsOpen(false);
    setCurrentFormat(format);

    console.log(`📄 ${action === 'preview' ? 'Preview' : 'Print'} - Format: ${format}`);
    console.log('Store Info FULL:', JSON.stringify(storeInfo, null, 2));
    console.log('Store Info Keys:', storeInfo ? Object.keys(storeInfo) : 'null/undefined');
    console.log('💰 Discount:', discount);
    console.log('📊 Custom VAT Rate:', customVATRate);

    // Check if storeInfo is properly loaded
    if (!storeInfo) {
      console.error('ERROR: Store info is null/undefined! Cannot generate receipt.');
      toast.error('Store information not loaded. Please refresh the page.');
      return;
    }

    let templateHtml;

    if (format === 'a4') {
      templateHtml = A4InvoiceTemplate(
        cart,
        customer,
        storeInfo,
        selectedPaymentMethod,
        paidAmount,
        receiptNumber,
        discount,
        customVATRate
      );
    } else {
      templateHtml = getThermalReceiptTemplate(
        cart,
        customer,
        storeInfo,
        selectedPaymentMethod,
        paidAmount,
        receiptNumber,
        discount,
        customVATRate
      );
    }

    console.log('📄 Generated Template (first 500 chars):', templateHtml.substring(0, 500));

    if (action === 'preview') {
      setPreviewHtml(templateHtml);
      setPreviewOpen(true);
    } else if (action === 'print') {
      await saveAndPrintDocument(templateHtml, format); // ✅ Use new function that saves first
    }
  };

  // ✅ NEW: Function to save ticket and then print (used when clicking print from print options)
  const saveAndPrintDocument = async (templateHtml, format) => {
    try {
      // ✅ STEP 1: Save ticket to database
      console.log('💾 Saving ticket to database...');
      
      const ticketParams = {
        cart,
        customer,
        selectedPaymentMethod,
        paidAmount,
        discount,
        customVATRate,
        receiptNumber,
        notes: `${format === 'a4' ? 'A4 Invoice' : 'Thermal Receipt'} - Printed on ${new Date().toLocaleString()}`
      };

      const ticketDto = transformCartToTicketDto(ticketParams);
      const validation = validateTicketData(ticketDto);

      if (!validation.isValid) {
        console.error('❌ Ticket validation failed:', validation.errors);
        toast.error(`Validation failed: ${validation.errors.join(', ')}`);
        return;
      }

      let createdTicket;
      try {
        createdTicket = await createTicket(ticketDto);
        console.log('✅ Ticket saved to database:', createdTicket);
        toast.success(`Ticket #${receiptNumber} saved successfully`);
      } catch (error) {
        console.error('❌ Failed to save ticket:', error);
        toast.error(`Failed to save ticket: ${error.message || 'Unknown error'}`);
        return;
      }

      // ✅ STEP 2: Print the document
      await printDocumentOnly(templateHtml, format);

      // ✅ STEP 3: Clear cart after successful print and save
      clearCart();

      console.log('✅ Save and print completed successfully');
      console.log('📊 Created Ticket ID:', createdTicket?.Id || 'N/A');
    } catch (error) {
      console.error('❌ Save and print error:', error);
      toast.error(t('print_failed') || 'Failed to print and save document');
    }
  };

  // ✅ NEW: Function to ONLY print (used when clicking print from preview dialog)
  const printDocumentOnly = async (templateHtml, format) => {
    try {
      const { jsPDF } = await import('jspdf');
      const html2canvas = (await import('html2canvas')).default;

      console.log(`🖨️ Generating ${format} PDF...`);

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = templateHtml;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.width = format === 'a4' ? '210mm' : '80mm';
      tempDiv.style.background = 'white';
      tempDiv.style.padding = format === 'a4' ? '20mm' : '10px';
      document.body.appendChild(tempDiv);

      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      document.body.removeChild(tempDiv);

      const imgData = canvas.toDataURL('image/png');
      
      let pdf;
      if (format === 'a4') {
        pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
        });
        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      } else {
        const pdfHeight = (canvas.height * 80) / canvas.width;
        pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: [80, pdfHeight],
        });
        pdf.addImage(imgData, 'PNG', 0, 0, 80, pdfHeight);
      }
      
      pdf.autoPrint();
      window.open(pdf.output('bloburl'), '_blank');
      
      console.log('✅ Document printed successfully');
      
      if (previewOpen) {
        setPreviewOpen(false);
      }
    } catch (error) {
      console.error('❌ Print error:', error);
      toast.error(t('print_failed') || 'Failed to print document');
      throw error; // Re-throw to be caught by caller
    }
  };

  // ✅ REMOVE the old printDocument function and replace with this:
// This is called from preview dialog's print button - only prints, doesn't save
const printDocument = async (templateHtml, format) => {
  await printDocumentOnly(templateHtml, format);
};

  // ✅ Updated getReceiptTemplate to use company info from Redux
  const getReceiptTemplate = () => {
    const totalExcl = cart.reduce((sum, item) => sum + item.Quantity * item.Price, 0);
    const totalVAT = cart.reduce((sum, item) => sum + (item.Quantity * item.Price * item.VAT) / 100, 0);
    const totalIncl = totalExcl + totalVAT;
    const change = (parseFloat(paidAmount) || 0) - totalIncl;

    const vatBreakdown = {};
    cart.forEach((item) => {
      const vatRate = parseFloat(item.VAT || 0);
      const itemTotal = item.Quantity * item.Price;
      const vatAmount = (itemTotal * vatRate) / 100;

      if (!vatBreakdown[vatRate]) {
        vatBreakdown[vatRate] = { base: 0, vat: 0 };
      }
      vatBreakdown[vatRate].base += itemTotal;
      vatBreakdown[vatRate].vat += vatAmount;
    });

    let vatSectionHtml = '';
    Object.keys(vatBreakdown).forEach((rate) => {
      const { base, vat } = vatBreakdown[rate];
      vatSectionHtml += `
        <tr>
          <td style="padding: 2px 0;">VAT ${rate}%:</td>
          <td style="text-align: right;">€${base.toFixed(2)}</td>
          <td style="text-align: right;">€${vat.toFixed(2)}</td>
        </tr>
      `;
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.4;
            color: #000;
            background: white;
            padding: 10px;
          }
          .receipt-container { width: 100%; }
          .header { text-align: center; margin-bottom: 15px; border-bottom: 2px dashed #000; padding-bottom: 10px; }
          .header h1 { font-size: 18px; font-weight: 600; margin-bottom: 5px; }
          .header p { font-size: 10px; margin: 2px 0; }
          .info-section { margin: 10px 0; font-size: 11px; }
          .info-row { display: flex; justify-content: space-between; margin: 3px 0; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0; }
          th { text-align: left; border-bottom: 1px solid #000; padding: 5px 0; font-size: 11px; }
          td { padding: 5px 0; font-size: 11px; }
          .item-row td { border-bottom: 1px dotted #ccc; }
          .total-section { margin-top: 15px; border-top: 2px solid #000; padding-top: 10px; }
          .total-row { display: flex; justify-content: space-between; margin: 5px 0; font-weight: 600; }
          .grand-total { font-size: 16px; margin-top: 10px; }
          .footer { text-align: center; margin-top: 20px; border-top: 2px dashed #000; padding-top: 10px; font-size: 10px; }
          .payment-info { margin: 10px 0; padding: 10px; background: #f5f5f5; border: 1px solid #ddd; }
        </style>
      </head>
      <body>
        <div class="receipt-container">
          <div class="header">
            <h1>TICKET DE CAISSE</h1>
            <p>${storeInfo?.Name || 'Your Store Name'}</p>
            <p>${storeInfo?.Address || '123 Street Address, City'}</p>
            <p>Tel: ${storeInfo?.Phone || storeInfo?.PhoneNumber || '+32 123 456 789'}</p>
            <p>TVA: ${storeInfo?.VATNumber || storeInfo?.TaxNumber || 'BE 0123.456.789'}</p>
            ${storeInfo?.Email ? `<p>Email: ${storeInfo.Email}</p>` : ''}
            ${storeInfo?.Website ? `<p>${storeInfo.Website}</p>` : ''}
          </div>

          <div class="info-section">
            <div class="info-row">
              <span>Receipt #:</span>
              <span><strong>${receiptNumber}</strong></span>
            </div>
            <div class="info-row">
              <span>Date:</span>
              <span>${new Date().toLocaleDateString('fr-BE')} ${new Date().toLocaleTimeString('fr-BE')}</span>
            </div>
            ${customer ? `
              <div class="info-row">
                <span>Client:</span>
                <span><strong>${customer.CompanyName || customer.Name || 'N/A'}</strong></span>
              </div>
            ` : ''}
            <div class="info-row">
              <span>Cashier:</span>
              <span>Admin</span>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th style="text-align: center;">Qty</th>
                <th style="text-align: right;">Price</th>
                <th style="text-align: right;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${cart.map(item => `
                <tr class="item-row">
                  <td>${item.Name || 'N/A'}</td>
                  <td style="text-align: center;">${item.Quantity}</td>
                  <td style="text-align: right;">€${item.Price.toFixed(2)}</td>
                  <td style="text-align: right;">€${(item.Quantity * item.Price).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="total-section">
            <div class="total-row">
              <span>Subtotal (Excl. VAT):</span>
              <span>€${totalExcl.toFixed(2)}</span>
            </div>
            
            <table style="font-size: 10px; margin: 10px 0;">
              <thead>
                <tr>
                  <th>VAT Rate</th>
                  <th style="text-align: right;">Base</th>
                  <th style="text-align: right;">VAT Amount</th>
                </tr>
              </thead>
              <tbody>
                ${vatSectionHtml}
              </tbody>
            </table>

            <div class="total-row">
              <span>Total VAT:</span>
              <span>€${totalVAT.toFixed(2)}</span>
            </div>
            
            <div class="total-row grand-total">
              <span>TOTAL (Incl. VAT):</span>
              <span>€${totalIncl.toFixed(2)}</span>
            </div>
          </div>

          <div class="payment-info">
            <div class="total-row">
              <span>Payment Method:</span>
              <span>${selectedPaymentMethod}</span>
            </div>
            ${paidAmount ? `
              <div class="total-row">
                <span>Paid:</span>
                <span>€${parseFloat(paidAmount).toFixed(2)}</span>
              </div>
              <div class="total-row">
                <span>Change:</span>
                <span>€${change.toFixed(2)}</span>
              </div>
            ` : ''}
          </div>

          <div class="footer">
            <p>*** THANK YOU FOR YOUR VISIT ***</p>
            <p>Keep this receipt for exchanges and returns</p>
            ${companyInfo?.Website ? `<p>${companyInfo.Website}</p>` : '<p>www.yourcompany.com</p>'}
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // ✅ Discount operations
  const handleApplyDiscount = (discountData) => {
    setDiscount(discountData);
    toast.success(
      `${t('discount_applied') || 'Discount applied'}: ${
        discountData.type === 'percentage'
          ? `${discountData.value}%`
          : `€${discountData.value.toFixed(2)}`
      }`
    );
  };

  const handleRemoveDiscount = () => {
    setDiscount(null);
    toast.info(t('discount_removed') || 'Discount removed');
  };

  // VAT handler
  const handleApplyVAT = (vatRate) => {
    if (vatRate === null || vatRate === undefined) {
      setCustomVATRate(21); // ✅ Reset to 21% instead of 0
      
      // Update all cart items to 21% VAT and recalculate base prices
      const updatedCart = cart.map(item => {
        const priceInclVAT = item.PriceInclVAT || item.Price * (1 + item.VAT / 100);
        const newBasePrice = priceInclVAT / (1 + 21 / 100);
        return {
          ...item,
          Price: newBasePrice,
          VAT: 21,
        };
      });
      setCart(updatedCart);
      
      toast.info(t('vat_reset') || 'VAT rate reset to 21%');
    } else {
      setCustomVATRate(vatRate);
      
      // Update VAT for all items and recalculate base prices
      const updatedCart = cart.map(item => {
        const priceInclVAT = item.PriceInclVAT || item.Price * (1 + item.VAT / 100);
        const newBasePrice = priceInclVAT / (1 + vatRate / 100);
        return {
          ...item,
          Price: newBasePrice,
          VAT: vatRate,
        };
      });
      setCart(updatedCart);
      
      toast.success(t('vat_applied') || `VAT rate changed to ${vatRate}%`);
    }
  };

  // ✅ Enhanced search with local-first, then DB fallback (minimum 4 characters for DB)
  const handleSearch = async (searchValue = searchQuery) => {
    if (!searchValue.trim()) {
      // If search is empty, show all local products
      setFilteredProducts(products);
      return;
    }

    const query = searchValue.toLowerCase().trim();
    
    // ✅ STEP 1: Search locally first (no minimum length requirement)
    let localResults = products.filter(p =>
      (p.Name || '').toLowerCase().includes(query) ||
      (p.SKU || '').toLowerCase().includes(query) ||
      (p.BarCode || '').toLowerCase().includes(query) ||
      (p.Description || '').toLowerCase().includes(query) ||
      (p.ProductCategory?.CategoryName || '').toLowerCase().includes(query)
    );

    console.log(`🔍 Local search for "${searchValue}":`, localResults.length, 'results');

    // ✅ If local results found, use them
    if (localResults.length > 0) {
      setFilteredProducts(localResults);
      return;
    }

    // ✅ STEP 2: Check if search query has minimum 4 characters before searching DB
    if (searchValue.length < 4) {
      console.log(`⚠️ Search query too short (${searchValue.length} chars). Need at least 4 characters for database search.`);
      setFilteredProducts([]); // Show no results for short queries with no local matches
      toast.info(t('search_min_4_chars') || 'Please enter at least 4 characters to search');
      return;
    }

    // ✅ STEP 3: No local results and query >= 4 chars, search database using pagination
    console.log(`🌐 No local results found, searching database for "${searchValue}"...`);
    setLoading(true);

    try {
      // ✅ Use paginated search for better performance
      const response = await getProductsPaginated({
        pageNumber: 1,
        pageSize: 50, // Limit search results to 50
        searchTerm: searchValue,
        sortBy: 'Name',
        sortDirection: 'Asc'
      });

      let dbResults = [];
      
      // ✅ Handle response structure
      if (response?.payload?.Items) {
        dbResults = response.payload.Items;
      } else if (response?.payload?.items) {
        dbResults = response.payload.items;
      } else if (response?.Items) {
        dbResults = response.Items;
      } else if (response?.items) {
        dbResults = response.items;
      } else if (response?.payload && Array.isArray(response.payload)) {
        dbResults = response.payload;
      } else if (response?.data && Array.isArray(response.data)) {
        dbResults = response.data;
      } else if (Array.isArray(response)) {
        dbResults = response;
      }

      // ✅ Map database results to match POS structure
      const mappedResults = dbResults.map(product => ({
        Id: product.Id,
        Name: product.Name,
        SKU: product.SKU || product.BarCode,
        BarCode: product.BarCode,
        Description: product.Description,
        SalePrice: product.SalePrice,
        CostPrice: product.CostPrice,
        MinSalePrice: product.MinSalePrice,
        AveragePrice: product.AveragePrice,
        StockQuantity: product.StockQuantity,
        MinStockLevel: product.MinStockLevel,
        IsActive: product.IsActive,
        IsPromotionProduct: product.IsPromotionProduct,
        PromotionPercentage: product.PromotionPercentage,
        ProductCategoryId: product.ProductCategoryId || product.CategoryId,
        ProductCategory: product.ProductCategory || (product.CategoryName ? {
          CategoryName: product.CategoryName
        } : null),
        SupplierId: product.SupplierId,
        Supplier: product.Supplier || (product.SupplierName ? {
          Name: product.SupplierName
        } : null),
        ProductBrandId: product.ProductBrandId || product.BrandId,
        ProductBrand: product.ProductBrand || (product.BrandName ? {
          BrandName: product.BrandName
        } : null),
        ModelNumber: product.ModelNumber,
        ImageUrl: product.ImageUrl,
        SyncToShopify: product.SyncToShopify,
        IsWishlistProduct: product.IsWishlistProduct,
        Price: product.SalePrice,
      }));

      console.log(`✅ Database search results:`, mappedResults.length, 'products');

      if (mappedResults.length > 0) {
        // ✅ Merge database results with local products (avoid duplicates)
        const existingIds = new Set(products.map(p => p.Id));
        const newProducts = mappedResults.filter(p => !existingIds.has(p.Id));
        
        if (newProducts.length > 0) {
          console.log(`➕ Adding ${newProducts.length} new products from database to local store`);
          const updatedProducts = [...products, ...newProducts];
          setProducts(updatedProducts);
        }
        
        setFilteredProducts(mappedResults);
        toast.success(`${t('found') || 'Found'} ${mappedResults.length} ${t('products') || 'product(s)'}`);
      } else {
        // ✅ No results in database either
        setFilteredProducts([]);
        toast.info(`${t('no_products_found_for') || 'No products found for'} "${searchValue}"`);
      }

    } catch (error) {
      console.error('❌ Database search error:', error);
      toast.error(t('search_failed') || 'Failed to search products');
      setFilteredProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Update useEffect for debounced search - Remove products dependency to avoid infinite loop
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (searchQuery !== '') {
        handleSearch(searchQuery);
      } else {
        // Show all local products when search is cleared
        setFilteredProducts(products);
      }
    }, 500); // 500ms delay for debouncing

    return () => clearTimeout(delayedSearch);
  }, [searchQuery]); // ✅ Only depend on searchQuery, not products

  // ✅ Update filterProducts to work properly with search
  useEffect(() => {
    if (!searchQuery) {
      filterProducts();
    }
  }, [selectedCategory, products]); // ✅ Only filter when category or products change, not search

  // ✅ Add toggleFullscreen function after state declarations
  const toggleFullscreen = () => {
    const elem = posContainerRef.current;
    
    if (!document.fullscreenElement && 
        !document.webkitFullscreenElement && 
        !document.mozFullScreenElement && 
        !document.msFullscreenElement) {
      // Enter fullscreen
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  const totalCartItems = cart.reduce((sum, item) => sum + item.Quantity, 0);

  return (
    <Box
      ref={posContainerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: isFullscreen ? '100vh' : 'calc(100vh - 64px)',
        bgcolor: '#f5f7fa',
        overflow: 'hidden',
        position: isFullscreen ? 'fixed' : 'relative',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: isFullscreen ? 9999 : 'auto',
        width: '100%',
      }}
    >
      {/* Header Bar */}
      <Paper
        elevation={2}
        sx={{
          position: 'relative',
          zIndex: 1100,
          px: { xs: 1, sm: 2 },
          py: { xs: 1, sm: 1.5 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 2 },
          bgcolor: 'white',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
          {(isMobile || isTablet) && (
            <IconButton 
              onClick={() => setCategoryDrawerOpen(true)}
              size={isSmallScreen ? "small" : "medium"}
            >
              <MenuIcon />
            </IconButton>
          )}
          {!isFullscreen && (
            <IconButton 
              onClick={() => navigate(-1)}
              size={isSmallScreen ? "small" : "medium"}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          {!isSmallScreen && (
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('point_of_sale') || 'POS'}
            </Typography>
          )}
        </Box>

        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', px: { xs: 0.5, sm: 2 } }}>
          <TextField
            size="small"
            placeholder={t('search_products') || 'Search...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: '100%',
              maxWidth: 600,
              bgcolor: 'white',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
                py: { xs: 0.75, sm: 1 },
              },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1.5 } }}>
          <Tooltip title={isFullscreen ? t('exit_fullscreen') : t('enter_fullscreen')}>
            <IconButton
              onClick={toggleFullscreen}
              size={isSmallScreen ? "small" : "medium"}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>

          {!isSmallScreen && (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {receiptNumber}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Main Content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
        {/* Category Panel */}
        {(isMobile || isTablet) ? (
          <Drawer
            anchor="left"
            open={categoryDrawerOpen}
            onClose={() => setCategoryDrawerOpen(false)}
            container={isFullscreen ? posContainerRef.current : undefined}
            style={{ position: isFullscreen ? 'absolute' : undefined }}
            PaperProps={{
              sx: { width: { xs: 240, sm: 280 } }
            }}
          >
            <CategoryPanel
              categories={categories}
              selectedCategory={selectedCategory}
              onSelectCategory={handleCategorySelect}
            />
          </Drawer>
        ) : (
          <CategoryPanel
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={handleCategorySelect}
          />
        )}

        {/* Product Grid */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 1, sm: 2 } }}>
          <ProductGrid
            products={filteredProducts}
            loading={loading}
            onSelectProduct={addToCart}
            showCustomProduct={true}
            onCustomProductClick={() => setProductDialogOpen(true)}
            isFullscreen={isFullscreen}
            fullscreenContainer={posContainerRef.current}
          />
        </Box>

        {/* Cart Panel */}
        {(isMobile || isTablet) ? (
          <>
            <Zoom in={true}>
              <Fab
                color="primary"
                onClick={() => setCartDrawerOpen(true)}
                sx={{
                  position: 'fixed',
                  bottom: { xs: 16, sm: 24 },
                  right: { xs: 16, sm: 24 },
                  zIndex: 1000,
                }}
              >
                <Badge badgeContent={totalCartItems} color="error" max={99}>
                  <ShoppingCartIcon />
                </Badge>
              </Fab>
            </Zoom>

            <Drawer
              anchor="right"
              open={cartDrawerOpen}
              onClose={() => setCartDrawerOpen(false)}
              container={isFullscreen ? posContainerRef.current : undefined}
              style={{ position: isFullscreen ? 'absolute' : undefined }}
              PaperProps={{
                sx: { width: { xs: '100%', sm: 400 } }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <IconButton onClick={() => setCartDrawerOpen(false)} sx={{ mr: 1 }}>
                  <ChevronRightIcon />
                </IconButton>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  {t('cart') || 'Cart'} ({totalCartItems})
                </Typography>
              </Box>
              <CartPanel
                cart={cart}
                customer={customer}
                selectedPaymentMethod={selectedPaymentMethod}
                paidAmount={paidAmount}
                receiptNumber={receiptNumber}
                discount={discount}
                customVATRate={customVATRate}
                vatRates={vatRates}
                onUpdateQuantity={updateCartQuantity}
                onRemoveItem={removeFromCart}
                onClearCart={clearCart}
                onSelectCustomer={() => setCustomerDialogOpen(true)}
                onPaymentMethodChange={setSelectedPaymentMethod}
                onPaidAmountChange={setPaidAmount}
                onPreview={handlePreview}
                onPrint={handlePrint}
                onApplyDiscount={handleApplyDiscount}
                onRemoveDiscount={handleRemoveDiscount}
                onApplyVAT={handleApplyVAT}
              />
            </Drawer>
          </>
        ) : (
          <CartPanel
            cart={cart}
            customer={customer}
            selectedPaymentMethod={selectedPaymentMethod}
            paidAmount={paidAmount}
            receiptNumber={receiptNumber}
            discount={discount}
            customVATRate={customVATRate}
            vatRates={vatRates}
            onUpdateQuantity={updateCartQuantity}
            onRemoveItem={removeFromCart}
            onClearCart={clearCart}
            onSelectCustomer={() => setCustomerDialogOpen(true)}
            onPaymentMethodChange={setSelectedPaymentMethod}
            onPaidAmountChange={setPaidAmount}
            onPreview={handlePreview}
            onPrint={handlePrint}
            onApplyDiscount={handleApplyDiscount}
            onRemoveDiscount={handleRemoveDiscount}
            onApplyVAT={handleApplyVAT}
          />
        )}
      </Box>

      {/* Dialogs */}
      <PrintOptionsDialog
        open={printOptionsOpen}
        onClose={() => setPrintOptionsOpen(false)}
        onSelectFormat={handleFormatSelection}
        container={isFullscreen ? posContainerRef.current : undefined}
        style={{ position: isFullscreen ? 'absolute' : undefined }}
      />

      <CustomerDialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        onSelectCustomer={(selectedCustomer) => {
          setCustomer(selectedCustomer);
          setCustomerDialogOpen(false);
        }}
        currentCustomer={customer}
        container={posContainerRef.current}
        style={{ position: 'absolute' }}
      />

      {currentFormat === 'a4' ? (
        <A4PreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          htmlContent={previewHtml}
          onPrint={() => printDocument(previewHtml, currentFormat)}
          container={isFullscreen ? posContainerRef.current : undefined}
          style={{ position: isFullscreen ? 'absolute' : undefined }}
        />
      ) : (
        <PreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          htmlContent={previewHtml}
          onPrint={() => printDocument(previewHtml, currentFormat)}
          format="receipt"
          container={isFullscreen ? posContainerRef.current : undefined}
          style={{ position: isFullscreen ? 'absolute' : undefined }}
        />
      )}

      <AddProductDialog
        open={productDialogOpen}
        onClose={() => setProductDialogOpen(false)}
        onProductCreated={handleProductCreated}
        suppliersFromStore={suppliersFromStore}
        brandsFromStore={brandsFromStore}
        categoriesFromStore={categoriesFromStore}
        onSupplierCreated={(supplier) => dispatch(addSupplierToStore(supplier))}
        onBrandCreated={(brand) => dispatch(addBrandToStore(brand))}
        onCategoryCreated={(category) => dispatch(addCategoryToStore(category))}
      />
    </Box>
  );
};

export default PosListPage;